import { useOktaAuth } from '@okta/okta-react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { debounce, padStart, replace, toLower } from 'lodash-es';
import type { ComponentType } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { DialogActions, DialogContent, DialogContentText, DialogTitle, SvgIcon, lighten, styled } from '@mui/material';

import type { RootState } from 'store';

import { ReactComponent as TimeOutEllipseHighIcon } from 'img/timeout-ellipse-high.svg';
import { ReactComponent as TimeOutEllipseLowIcon } from 'img/timeout-ellipse-low.svg';
import { ReactComponent as TimeOutEllipseMediumIcon } from 'img/timeout-ellipse-medium.svg';
import { ReactComponent as TimeOutIcon } from 'img/timeout.svg';

import { AuthActions } from 'common/auth/actions';
import { AuthUserType } from 'common/graphql/types';
import { useClient } from 'common/hooks/use-client';
import { useOnAnyActivity } from 'common/hooks/useOnAnyActivity';
import { resetApplicationLanguage } from 'common/i18n/i18n';
import { CILogger } from 'common/log/log.provider';
import { removeOnUnloadEventListener } from 'common/utils/common.utils';
import { useInterval } from 'common/utils/hooks';
import { DialogPrompt } from 'common/viewcomp/dialog/DialogPrompt';
import { CenterText } from 'module/activity/components/styled/CenterText';
import { guardedOktaSignInWithRedirect } from 'module/auth/okta/utils/oktaUtils';

import { CISecondaryButton, CITertiaryButton } from '../forms/buttons';
import { CIPageStatus } from '../general/ci_pagestatus';

import {
    saveLastPagesOpenedAndLogout,
    TimeoutConfigCognito,
    TimeoutConfigOkta,
    removePatientBannerSessionStorage,
} from './helpers';

interface SessionState {
    docTitle: string;
    openSessionPopup: boolean;
    timerCountDownInSec: number;
    pendingForceLogout: boolean;
}

export const SessionTimeoutDialog = () => {
    const { oktaAuth: oktaAuthInstance } = useOktaAuth();
    const auth = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch();
    const client = useClient();
    const { t } = useTranslation('global');

    const isOktaLogin =
        auth?.activeUser?.userType !== AuthUserType.PATIENT && auth?.activeUser?.authenticationMethod === 'OKTA';

    const totalSessionDuration = isOktaLogin
        ? TimeoutConfigOkta.totalSessionDuration
        : TimeoutConfigCognito.totalSessionDuration;

    const [session, setSession] = useState<SessionState>({
        docTitle: document.title,
        openSessionPopup: false,
        timerCountDownInSec: TimeoutConfigCognito.dialogDuration,
        pendingForceLogout: false,
    });

    const openPopup = (countdown: number) => {
        setSession((prev) => ({
            ...prev,
            docTitle: document.title,
            openSessionPopup: true,
            timerCountDownInSec: countdown,
        }));
    };

    const closePopup = (_?: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
        if (reason === 'backdropClick') {
            return false;
        }
        document.title = session.docTitle;
        setSession((prev) => ({
            ...prev,
            openSessionPopup: false,
        }));
    };

    const updatePopupCountdown = (countdown: number) => {
        document.title = document.title === t('session.title') ? session.docTitle : t('session.title');
        setSession((prev) => ({
            ...prev,
            timerCountDownInSec: countdown,
        }));
    };

    const applicationLogout = () => dispatch(AuthActions.logoutRequest());
    const logoutEndingOktaSessionLocally = () => dispatch(AuthActions.logoutRequest({ endOktaSessionLocally: true }));

    const userChoseToStay = () => {
        resetTimer();
        closePopup();
    };

    const userChoseToLogout = () => {
        removeOnUnloadEventListener();
        applicationLogout();
        resetApplicationLanguage();
        removePatientBannerSessionStorage();
    };

    const forceLogout = useCallback(async () => {
        removePatientBannerSessionStorage();
        if (!isOktaLogin) {
            saveLastPagesOpenedAndLogout(auth, client, applicationLogout);

            return;
        }

        const sessionInfo = (await oktaAuthInstance.session.get()) as unknown as {
            login: string;
            status: string;
            expiresAt: string;
        };

        if (!sessionInfo || sessionInfo.status !== 'ACTIVE') {
            CILogger.getInstance().info('Session timed out and invalid okta session detected.');

            saveLastPagesOpenedAndLogout(auth, client, logoutEndingOktaSessionLocally);

            return;
        }

        const oktaLogin = sessionInfo?.login;
        const activeUserName = replace(auth?.activeUser?.name || '', /^FX_/, '');
        const isSameUser = toLower(oktaLogin) === toLower(activeUserName);

        // different user detected in okta, logout and sign in with new user
        if (!isSameUser && auth?.activeUser?.name) {
            CILogger.getInstance().info(
                `Session timed out but different user detected in okta, logging out: ' + ${activeUserName} and signing in user: ' + ${oktaLogin}`
            );

            saveLastPagesOpenedAndLogout(auth, client, logoutEndingOktaSessionLocally);

            await guardedOktaSignInWithRedirect(oktaAuthInstance);
            return;
        }

        CIPageStatus.success(t('session.refreshed'));
        CILogger.getInstance().info(`Local session timed out but refreshed from okta.`);
        session.pendingForceLogout = false;
        resetTimer();
        closePopup();
    }, [auth, client, applicationLogout, logoutEndingOktaSessionLocally, oktaAuthInstance]);

    const debouncedForceLogout = useCallback(debounce(forceLogout, 10000, { leading: true, trailing: false }), [
        forceLogout,
    ]);
    const handleUserActivity = useCallback(() => {
        if (session.pendingForceLogout) {
            debouncedForceLogout();

            return;
        }

        // Don't reset timer if dialog is already open - user must choose to stay logged in
        if (session.openSessionPopup) {
            return;
        }

        resetTimer();
    }, [session.pendingForceLogout, session.openSessionPopup, debouncedForceLogout]);

    const checkTimeoutLoop = (isLoad = false) => {
        if (!auth.activeMeeting) {
            const lastActivityTime = +localStorage.getItem(TimeoutConfigCognito.storageKey) || resetTimer();
            const logoutRemainingSec = remainingSecondsForLogout(lastActivityTime, totalSessionDuration);
            const dialogPopRemainingSec = remainingSecondsForDialogPop(lastActivityTime, totalSessionDuration);

            if (!session.openSessionPopup && dialogPopRemainingSec === 0) {
                openPopup(logoutRemainingSec);
            } else if (session.openSessionPopup && dialogPopRemainingSec === 0) {
                updatePopupCountdown(logoutRemainingSec);
            } else if (session.openSessionPopup && dialogPopRemainingSec > 0) {
                closePopup();
            }

            if (isLoad && dialogPopRemainingSec > 0) {
                resetTimer();
            }

            if (isOktaLogin && document.visibilityState === 'hidden') {
                // when page isn't visible we relay on okta session
                resetTimer();
            }

            if (logoutRemainingSec === 0 && !session.pendingForceLogout) {
                setSession((prev) => ({
                    ...prev,
                    pendingForceLogout: true,
                }));
            }
        }
    };

    useInterval(checkTimeoutLoop, TimeoutConfigCognito.intervalInMs);

    const resetTimer = () => {
        if (!session.openSessionPopup) {
            const currentTime = getSecondsNow();

            localStorage.setItem(TimeoutConfigCognito.storageKey, String(currentTime));

            return currentTime;
        }
    };

    useEffect(() => {
        checkTimeoutLoop(true);
    }, []);

    // Resets the timer whenever there is a movement/operation performed in the app
    useOnAnyActivity(handleUserActivity);

    return (
        <DialogPrompt open={session.openSessionPopup} onClose={closePopup}>
            <CenterText>
                <DialogTitleWithIcon id="session_timeout_title">
                    <div style={{ position: 'relative', marginBottom: 20 }}>
                        <DialogIcon>
                            <SvgIcon component={TimeOutIcon} />
                        </DialogIcon>
                        {session.timerCountDownInSec >= 60 ? (
                            <SeverityIcon component={TimeOutEllipseLowIcon} viewBox="0 0 64 64" />
                        ) : session.timerCountDownInSec > 30 ? (
                            <SeverityIcon component={TimeOutEllipseMediumIcon} viewBox="0 0 64 64" />
                        ) : (
                            <SeverityIcon component={TimeOutEllipseHighIcon} viewBox="0 0 64 64" />
                        )}
                    </div>
                    {t('session.title')}
                </DialogTitleWithIcon>
                <DialogContent style={{ width: 380 }}>
                    <DialogContentText id="session_timeout_instructions">
                        <StyledText>
                            {parse(
                                DOMPurify.sanitize(
                                    t('session.instructions', {
                                        timeOutDuration: formatTime(session.timerCountDownInSec),
                                    })
                                )
                            )}
                        </StyledText>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LogoutButton
                        id="session_log_out_dialog"
                        onClick={userChoseToLogout}
                        disabled={session.timerCountDownInSec === 0}
                    >
                        {t('session.log_out')}
                    </LogoutButton>
                    <CISecondaryButton
                        id="stay_logged_in_dialog"
                        onClick={userChoseToStay}
                        disabled={session.timerCountDownInSec === 0}
                    >
                        {t('session.stay_log_in')}
                    </CISecondaryButton>
                </DialogActions>
            </CenterText>
        </DialogPrompt>
    );
};

const DialogIcon = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    border: '2px solid ' + theme.ciPalette.border.lightGray,
}));

const SeverityIcon: ComponentType<any> = styled(SvgIcon)({
    position: 'absolute',
    fill: 'transparent',
    width: '64px',
    height: '64px',
    top: 0,
    left: 0,
});

const DialogTitleWithIcon = styled(DialogTitle)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

const StyledText = styled('span')(({ theme }) => ({
    color: theme.ciPalette.text.fullBlack,
    fontSize: 14,

    '& span': {
        color: theme.ciPalette.error.ciErrorRed,
        fontWeight: 500,
    },
}));

const LogoutButton = styled(CITertiaryButton)(({ theme }) => ({
    color: theme.ciPalette.error.muiErrorRed,
    borderColor: theme.ciPalette.error.muiErrorRed,
    '&:hover': {
        backgroundColor: lighten(theme.ciPalette.error.muiErrorRed, 0.97),
        borderColor: theme.ciPalette.error.muiErrorRed,
    },
}));

const formatTime = (seconds: number) =>
    `${padStart(String(Math.floor(seconds / 60)), 2, '0')}:${padStart(String(seconds % 60), 2, '0')}`;

const getSecondsNow = () => Math.floor(Date.now() / 1000);

const remainingSecondsForDialogPop = (startTimeInSeconds: number, totalSessionDuration: number) =>
    Math.max(totalSessionDuration - TimeoutConfigCognito.dialogDuration - (getSecondsNow() - startTimeInSeconds), 0);

const remainingSecondsForLogout = (startTimeInSeconds: number, totalSessionDuration: number) =>
    Math.max(totalSessionDuration - (getSecondsNow() - startTimeInSeconds), 0);
